import React, { useMemo } from 'react';
import './payment-card.scss';
import { TouchableOpacity } from '../../common';
import {
  dispatch,
  getSelectedItems,
  Payment,
  updateSelectedItemsMap,
  useAppSelector,
} from '@zonofi/zono-money-store';
import { Divider, KeyValuePair, StatusBadge } from '../../components';
import { getPaymentStatusStyle, getPaymentStatusText } from '../../helper';
import { DividerColorVariant, PaymentStatusEnum } from '../../constants';
import moment from 'moment';
import { getCalculatedAmount } from '@zonofi/common/functions/format-amount';
import { getPaymentStatusIcon } from '../helper';
import { ReactComponent as Checkbox } from '@zonofi/svg-icons/checkbox.svg';
import { ReactComponent as Checked } from '@zonofi/svg-icons/checked.svg';

interface PaymentCardProps {
  item: Payment;
  onCardPress?: (item: Payment) => void;
  showSelection?: boolean;
}

export const PaymentCard: React.FC<PaymentCardProps> = ({
  item,
  onCardPress,
  showSelection,
}) => {
  const selectedPaymentItems = useAppSelector(getSelectedItems);
  const isSelected = selectedPaymentItems.some(
    (selected) => selected.id === item.id
  );

  const paymentValueInfo = useMemo(
    () => [
      {
        id: 0,
        title: 'Payment Date',
        value: item?.paymentDate
          ? moment(item.paymentDate).format('MMMM DD, YYYY')
          : 'N/A',
      },
      {
        id: 1,
        title: 'Type of Payment',
        value: item?.paymentMode ?? 'N/A',
      },
      {
        id: 2,
        title: 'Amount Paid',
        value:
          getCalculatedAmount(item?.amountPaid?.toString() || '0', true) ??
          'N/A',
      },
    ],
    [item?.paymentDate, item?.paymentMode, item?.amountPaid]
  );

  return (
    <TouchableOpacity
      className="payment-card-container"
      onPress={() => onCardPress?.(item)}
      disabled={showSelection && item?.status !== PaymentStatusEnum.PAID}
    >
      <div className="payment-card-view-v1">
        <div className="payment-card-view-v1--left">
          {showSelection &&
            (isSelected ? (
              <Checked
                className="payment-card-view-v1--checkbox"
                color={'#000000'}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  dispatch(updateSelectedItemsMap(item));
                }}
              />
            ) : (
              <Checkbox
                className="payment-card-view-v1--checkbox"
                color={'#8C8C8C'}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  dispatch(updateSelectedItemsMap(item));
                }}
              />
            ))}
          <img src={getPaymentStatusIcon(item?.status ?? '')} alt="" />
          <div className="payment-card-text">
            <span className="payment-card-text--ref font-semibold">
              {item?.paymentRefCode}
            </span>
            {item?.customerName && (
              <span className="payment-card-text--customer font-semibold">{`By ${item?.customerName}`}</span>
            )}
          </div>
        </div>
        <StatusBadge
          status={getPaymentStatusText(item?.status ?? '')}
          style={getPaymentStatusStyle(item?.status ?? '')}
        />
      </div>
      <Divider
        colorVariant={DividerColorVariant.SECONDARY}
        className="divider-style"
      />

      <div className="payment-card-view-v2">
        {paymentValueInfo.map((info) => (
          <KeyValuePair key={info.id} title={info.title} value={info.value} />
        ))}
      </div>
    </TouchableOpacity>
  );
};
